<template>
  <div>
    <div class="tools-strip">
      <button
        v-if="globalIsUserAllowed('Google_Street_View')"
        class="tool-btn"
        :class="{ 'tool-btn--active': showStreetView }"
        title="Google Street View"
        @click="toggleStreetView()"
      >
        <fa :icon="['fas', 'street-view']" size="sm"/>
      </button>

      <button
        v-if="globalIsUserAllowed('ANSI_Feature_Table')"
        class="tool-btn"
        title="Feature Table"
        @click="initFeatureTable()"
      >
        <fa :icon="['fas', 'table']" size="sm"/>
      </button>

      <button
        v-if="globalIsUserAllowed('ANSI_Reports')"
        class="tool-btn"
        title="Reports"
        @click="initANSIReports()"
      >
        <fa :icon="['fas', 'chart-pie']" size="sm"/>
      </button>

      <button
        v-if="mmp_layersObj['Otonomo'] && globalIsUserAllowed('Otonomo')"
        class="tool-btn"
        :class="{ 'tool-btn--active': checkForActiveOtonomoLayer() }"
        :disabled="!isNorthAmericaExtent"
        :title="isNorthAmericaExtent ? 'Otonomo' : 'Otonomo is only available for North America'"
        @click="openOtonomo()"
      >
        <fa :icon="['fas', 'road']" size="sm"/>
      </button>

      <button
        v-if="mmp_layersObj['Otonomo'] && globalIsUserAllowed('Otonomo_Status')"
        class="tool-btn"
        :class="{ 'tool-btn--active': showPopup }"
        title="Draw Selection"
        @click="enableDraw()"
      >
        <fa :icon="['fas', 'crop']" size="sm"/>
      </button>
    </div>

    <MMP_ANSI_Otonomo
      :previousSelectedSubcountry="previousSelectedSubcountry"
      :view="map.getView()"
      @enableOtonomoLayer="enableOtonomoLayer($event)"
      @subcountryChanged="$emit('subcountryChanged')"
    />
  </div>
</template>

<style scoped>
.tools-strip {
  display: flex;
  flex-direction: row;
  background: #063c6d;
  border-radius: 0.25rem;
  box-shadow: 0 2px 8px rgba(6, 60, 109, 0.25);
  overflow: hidden;
}

.tool-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width:40px;
  height: 30px;
  background: none;
  border: none;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255);
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
  flex-shrink: 0;
}

.tool-btn:last-child {
  border-right: none;
}

.tool-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.tool-btn--active {
  background: rgba(255, 255, 255, 0.32);
  color: #fff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.3);
}

.tool-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.tool-btn:disabled:hover {
  background: none;
  color: rgba(255, 255, 255);
}
</style>

<script>
import axios from 'axios'
import { createLayers } from '../../../utils/utils.js'
import { unByKey } from 'ol/Observable'
import { transform } from 'ol/proj'
import WKT from 'ol/format/WKT';
import CircleStyle from 'ol/style/Circle'
import { Draw, Snap } from 'ol/interaction'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Style from 'ol/style/Style'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Overlay from 'ol/Overlay.js'
import MMP_ANSI_Otonomo from './MMP_ANSI_Otonomo.vue'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'MMP_ANSI_Tools',
  components: {
    MMP_ANSI_Otonomo
  },
  computed: {
    ...mapGetters(['globalUserRole', 'globalAllowedUserModules', 'globalIsUserAllowed']),
    // Otonomo subcountry data only exists for North America (extent_id 5).
    isNorthAmericaExtent() {
      return this.selectedExtent === 'North America'
    }
  },
  props: [
    'ansiLayersButtonCtrl',
    'apiUrl',
    'currentANSISelset',
    'layerDefinition',
    'map',
    'mmp_layersObj',
    'otonomoFeatureTableSubcountry',
    'selectedExtent',
    'showStreetView',
    'currentAnsiSegment',
    'currentANSIPartNumber',
    'user',
    'otonomoEditedSubcountry',
    'otonomoFilters'
  ],
  data() {
    return {
      ansi: false,
      ansiCustomerName: null,
      ansiLayer: null,
      childAnsiLayerName: null,
      getUrl: null,
      layerResponse: [],
      previousSelectedSubcountry: null,
      selected: null,
      selset: null,
      rotationTooltipVisible: false,
      showTooltip: false,
      tooltipText: 'Use Alt+Shift+Drag to rotate the map! Toggle the same button to reset the viewport.',
      wkt: null,
      showPopup: false,
      countforBulkOtonomo: 0
    }
  },
  methods: {
    booleanBtnClass(btn) {
      // btn is a passed in variable, either ansi, measure, or $parent.showStreetView
      return btn ? 'primary' : 'info'
    },
    toggleStreetView() {
      this.$emit('toggleGSV', !this.showStreetView)
      if (!this.showStreetView) {
        new unByKey(this.olSelectkey)
        this.olSelectkey = this.map.on('singleclick', evt => {
          let coord = new transform(evt.coordinate, 'EPSG:3857', 'EPSG:4326')
          this.$emit('getGSV', coord)
        })
      }
    },
    async updateSelset(e) {
      this.selected = e.toString()
      this.$emit('getAnsiSelectedSelset', e.toString())
      if (this.layerDefinition == 'ANSI') {
        await this.getAnsiLayer()
        // Resolve the ANSI layer object. getAnsiLayer() derives the name from
        // ansiLayersButtonCtrl; if that lookup misses, fall back to scanning the
        // loaded layers for the ANSI / random_segments layer.
        let layerName = this.childAnsiLayerName
        let ansiLayerObj = layerName ? this.mmp_layersObj[layerName] : null
        if (!ansiLayerObj || !ansiLayerObj.layer) {
          layerName = Object.keys(this.mmp_layersObj || {}).find(
            k => k === 'ANSI' || k === 'random_segments'
          )
          ansiLayerObj = layerName ? this.mmp_layersObj[layerName] : null
        }
        if (!ansiLayerObj || !ansiLayerObj.layer) {
          console.warn('ANSI layer not available for selset update:', this.childAnsiLayerName)
          return
        }
        // Keep childAnsiLayerName in sync with the layer we actually found
        this.childAnsiLayerName = layerName
        const ansiLayer = ansiLayerObj.layer
        ansiLayer.getSource().updateParams({
          CQL_FILTER: "selset='" + e.toString() + "'"
        })
        /* This loadAttributes function call takes parameters as layer name,
        customer name, and ANSI layer data generated when selset is selected. */
        this.$emit('loadAttributes', {
          name: this.childAnsiLayerName,
          layer_group: this.ansiCustomerName,
          response: this.layerResponse
        })
        this.$emit('getLegend', this.childAnsiLayerName)
        this.ansi = true
      } else if (this.layerDefinition == 'BIASED') {
        let biasedLayers = this.ansiLayersButtonCtrl.filter(layer => layer.layer_definition == 'BIASED')
        biasedLayers.forEach(layer => {
          if (['Added', 'Diverging', 'End of Map', 'Ending', 'Merging', 'Start of Map'].includes(layer.name)) {
            let biased = this.mmp_layersObj[layer.name].layer
            biased.getSource().updateParams({
              CQL_FILTER: `part_number='${this.currentANSIPartNumber.part_number}'`
            })
          }
        })
      }
    },
    clearAnsiSelection() {
      this.selected = null
      this.ansi = false
    },
    initFeatureTable() {
      if (this.layerDefinition == 'BIASED') {
        this.$bvModal.show('biasedfeatureTableModal')
      }
      else {
        this.$bvModal.show('featureTableModal')
      }
    },
    getAnsiLayer() {
      let res = this.ansiLayersButtonCtrl.filter(
        f => f.name == 'random_segments' || f.name == 'ANSI'
      )
      if (res.length > 0) {
        this.childAnsiLayerName = res[0].name
        this.$emit('updateAnsiLayerName', this.childAnsiLayerName)
      }
    },
    initANSIReports() {
      this.getAnsiLayer()
      if (this.layerDefinition == "Intersections") {
        this.$emit('initIntersectionReports', this.selected)
      } else if (this.layerDefinition == 'BIASED') {
        this.$emit('initBIASEDReports', this.selected)
      } else {
        this.$emit('initANSIReports', this.selected)
      }
    },
    openOtonomo() {
      this.$bvModal.show('otonomoSubcountryModal')
    },
    enableOtonomoLayer(selectedSubcountry) {
      let otonomoLayer = null
      let editedSubcountry =
        selectedSubcountry
          ? selectedSubcountry.split('_')[1]
            ? selectedSubcountry.split('_').slice(1).join('').toLowerCase()
            : selectedSubcountry.toLowerCase()
          : null

      // Check if Otonomo layer exists on map already
      this.map.getLayers().forEach(layer => {
        if (layer.get('name') == 'Otonomo') {
          otonomoLayer = layer
        }
      })

      // If Otonomo layer exists on map, modify it
      if (otonomoLayer) {
        // If editedSubcountry isn't null, modify layer
        if (editedSubcountry) {
          // Get the attributes for the Otonomo layer
          this.$emit('setOtonomoSubcountryNames', {
            layer: otonomoLayer,
            subcountry: selectedSubcountry,
            editedSubcountry: editedSubcountry
          })
        } else {
          // If editedSubcountry is null, remove layer from the map and toggle off the layer
          this.map.removeLayer(otonomoLayer)
          this.$emit('otonomoToggledOff')
        }
      } else {
        // If Otonomo layer doesn't exist on map, create it
        axios
          .get(
            process.env.API_LOCATION + '/api/layer/getOtonomoLayer',
            {
              headers: {
                'x-auth-token': sessionStorage.token
              }
            }
          )
          .then(response => {
            const content = response.data
            if (content.errors) {
              console.log('Otonomo Subcountry API Call Error: ' + content.errors)
            } else {
              let layerLinks = []
              content.rows
                .forEach(link => {
                  let linkObj = link.link_object
                  // Replaces all underscores with spaces
                  let output = linkObj.name.replace(/_/g, ' ')
                  linkObj['display_name'] = output
                  layerLinks.push(linkObj)
                })
              otonomoLayer = createLayers(layerLinks, this.map)['Otonomo'].layer
              // Emit the Otonomo layer names to MMP.vue
              this.$emit('setOtonomoSubcountryNames', {
                layer: otonomoLayer,
                subcountry: selectedSubcountry,
                editedSubcountry: editedSubcountry
              })
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      }
      // Remember the selected subcountry for when loading Otonomo component after closing
      this.previousSelectedSubcountry = selectedSubcountry
    },
    checkForActiveOtonomoLayer() {
      let retVal = false
      this.map.getLayers().forEach(layer => {
        if (layer.get('name') == 'Otonomo') {
          retVal = true
        }
      })
      return retVal
    },
    rotationClick() {
      this.showTooltip = !this.showTooltip
      if (!this.showTooltip) {
        this.map.getView().setRotation(0)
      }
    },
    enableDraw() {

      this.showPopup = !this.showPopup;
      if (!this.source) {
        this.source = new VectorSource();
        this.vectorLayer = new VectorLayer({
          source: this.source,
          style: new Style({
            fill: new Fill({ color: 'rgba(255, 255, 0, 0.5)' }),
            stroke: new Stroke({ color: '#ffff00', width: 4 }),
            image: new CircleStyle({
              radius: 7,
              fill: new Fill({ color: '#ffff00' })
            })
          })
        });

        this.map.addLayer(this.vectorLayer);
        this.vectorLayer.setZIndex(4);
        this.draw = new Draw({
          source: this.source,
          type: 'Polygon'
        });
        this.snap = new Snap({ source: this.source });
        this.map.addInteraction(this.draw);
        this.map.addInteraction(this.snap);
        // Disable initially
        this.draw.setActive(false);
        this.snap.setActive(false);
        // Draw End Event
        this.draw.on('drawend', (evt) => {
          const format = new WKT();
          this.polygonWkt = format.writeGeometry(
            evt.feature.getGeometry(),
            { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
          );
          this.wkt = this.polygonWkt;
          evt.feature.setStyle(new Style({
            fill: new Fill({ color: 'rgba(255, 255, 200, 0.5)' }),
            stroke: new Stroke({ color: '#ffff00', width: 4 })
          }));
          // Disable drawing after one polygon
          this.draw.setActive(false);
          this.createPopup(evt.feature);
        });
      }
      // TOGGLE ACTIVE STATE
      this.draw.setActive(this.showPopup);
      this.snap.setActive(this.showPopup);
      // If turning OFF → clear everything
      if (!this.showPopup) {
        if (this.source) this.source.clear();
        if (this.overlay) this.map.removeOverlay(this.overlay);
      }
    },
    async createPopup(feature) {
      await this.getCountforBulkOtonomo(this.wkt);
      const geom = feature.getGeometry();
      const extent = geom.getExtent();
      const sidePosition = [extent[2], (extent[1] + extent[3]) / 2];

      const popupContainer = document.createElement('div');
      popupContainer.className = 'polygon-popup';
      popupContainer.style.background = 'white';
      popupContainer.style.padding = '18px';
      popupContainer.style.height = '215px';
      popupContainer.style.width = '220px';
      popupContainer.style.borderRadius = '10px';
      popupContainer.innerHTML = `
    <p style="color:#007bff"><i> ${this.countforBulkOtonomo} </i> Signs are selected.</p>
    <p>Change Status</p>
    <label><input type="radio" name="polygonOption" value="true"> Active</label><br>
    <label><input type="radio" name="polygonOption" value="false"> Inactive</label><br>
    <button id="popupSubmit" class="primary-btn">Submit</button>
    <button id="popupClear" class="primary-btn">Clear</button>
  `;
      // Add some CSS for styling
      const style = document.createElement('style');
      style.innerHTML = `
  .polygon-popup .primary-btn {
    background-color: #007bff; 
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  }
  .polygon-popup .primary-btn:hover {
    background-color: #0056b3; /* darker blue on hover */
  }
`;
      document.head.appendChild(style);
      this.overlay = new Overlay({
        element: popupContainer,
        positioning: 'center-left',
        stopEvent: true,
        offset: [10, 0],
      });
      this.map.addOverlay(this.overlay);
      this.overlay.setPosition(sidePosition);
      popupContainer.querySelector('#popupSubmit').addEventListener('click', () => {
        const selected = popupContainer.querySelector('input[name="polygonOption"]:checked');
        if (!selected) {
          alert("Please select an option.");
          return;
        }
        this.updateBulkOtonomo(this.wkt, selected.value);
        this.source.clear();
        this.map.removeOverlay(this.overlay);
        this.showPopup = false;
      });
      popupContainer.querySelector('#popupClear').addEventListener('click', () => {
        this.source.clear();
        this.map.removeOverlay(this.overlay);
        this.$bvToast.toast('Cleared.', {
          autoHideDelay: 5000,
          variant: 'success',
          solid: true,
          title: 'Selection Cleared from the map.',
          toaster: 'b-toaster-bottom-center'
        })
        this.showPopup = false;
      });
    },
    updateBulkOtonomo(wkt, status) {
      if (sessionStorage.token) {
        axios
          .get(
            process.env.API_LOCATION +
            '/api/layer/updateBulkOtonomoSign',
            {
              headers: {
                'x-auth-token': sessionStorage.token
              },
              params: {
                tableName: this.otonomoEditedSubcountry,
                status: status,
                wkt: wkt,
                username: this.user.name,
                otonomoFilters: JSON.stringify(this.otonomoFilters)
              }
            }
          )
          .then(response => {
            const content = response.data
            if (content.errors) {
              console.log('Otonomo sign update Error: ' + content.errors)
            }
            else {
              this.$bvToast.toast('Status saved, Please Zoom-in/Zoom-out to reflect the change on the map', {
                autoHideDelay: 5000,
                variant: 'success',
                solid: true,
                title: 'Data saved successfully.',
                toaster: 'b-toaster-bottom-center'
              })
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        console.log('No User Token')
      }

    },
    async getCountforBulkOtonomo(wkt) {
      if (sessionStorage.token) {
        await axios
          .get(
            process.env.API_LOCATION +
            '/api/layer/getCountforBulkOtonomo',
            {
              headers: {
                'x-auth-token': sessionStorage.token
              },
              params: {
                tableName: this.otonomoEditedSubcountry,
                wkt: wkt,
                otonomoFilters: JSON.stringify(this.otonomoFilters)
              }
            }
          )
          .then(response => {
            const content = response.data
            if (content.errors) {
              console.log('Otonomo sign update Error: ' + content.errors)
            }
            else {
              this.countforBulkOtonomo = content.rows[0].count
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        console.log('No User Token')
      }

    }

  },
  watch: {
    currentANSISelset: function (newValue, oldValue) {
      this.previousSelectedSubcountry = null
      this.updateSelset(newValue)
    },
    otonomoFeatureTableSubcountry: function (newValue, oldValue) {
      if (newValue) {
        this.enableOtonomoLayer(newValue)
      }
    }
  },
  mounted() {
    this.$emit('initResize')
  },
  beforeDestroy() {
    if (this.measure) {
      this.toggleMeasure()
    }
  }
}
</script>
